import moment from 'moment';
import clsx from 'clsx';
import { AlertTriangle } from 'lucide-react';

import { useLicenseInfo } from '@/react/portainer/licenses/use-license.service';
import { pluralize } from '@/portainer/helpers/strings';
import { LicenseType } from '@/react/portainer/licenses/types';
import { licenseRenewURL } from '@/react/portainer/licenses/constants';

import styles from './LicenseExpirationPanel.module.css';

export function LicenseExpirationPanelContainer() {
  const licenseInfoQuery = useLicenseInfo();

  if (licenseInfoQuery.isLoading || !licenseInfoQuery.info) {
    return null;
  }

  const nextLicenseExpiryUnix = moment.unix(
    licenseInfoQuery.info?.expiresAt || 0
  );
  const remainingDays = nextLicenseExpiryUnix.diff(
    moment().startOf('day'),
    'days'
  );
  const noValidLicense = !licenseInfoQuery.info?.valid;

  return (
    <LicenseExpirationPanel
      remainingDays={remainingDays}
      noValidLicense={noValidLicense}
      licenseType={licenseInfoQuery.info.type}
    />
  );
}

interface Props {
  remainingDays: number;
  noValidLicense?: boolean;
  licenseType: LicenseType;
}

export function LicenseExpirationPanel({
  remainingDays,
  noValidLicense,
  licenseType,
}: Props) {
  if (remainingDays > 30) {
    return null;
  }

  const expirationMessage = buildMessage(
    remainingDays,
    noValidLicense,
    licenseType
  );

  return (
    <div
      className={clsx(
        styles.container,
        '!mt-4 th-highcontrast:border-white th-highcontrast:bg-black'
      )}
    >
      <div className={clsx(styles.item, 'vertical-center')}>
        <AlertTriangle className="icon icon-sm icon-warning shrink-0" />
        <span className="text-muted">{expirationMessage}</span>
      </div>
    </div>
  );
}

function buildMessage(
  days: number,
  noValidLicense?: boolean,
  licenseType?: LicenseType
) {
  let contactMessage = <>Please contact Portainer to purchase a license.</>;
  if (licenseType === LicenseType.Free) {
    contactMessage = (
      <>
        Please fill out <a href={licenseRenewURL}>the form</a> to upgrade your
        license.
      </>
    );
  }

  if (noValidLicense) {
    return (
      <>
        You have no valid licenses and will need to supply one on next login.{' '}
        {contactMessage}
      </>
    );
  }

  const expiringText = buildExpiringText(days);

  return (
    <>
      One or more of your licenses {expiringText}. {contactMessage}
    </>
  );

  function buildExpiringText(days: number) {
    if (days < 0) {
      return 'has expired';
    }

    if (days === 0) {
      return 'expires TODAY';
    }

    return `will expire in ${days} ${pluralize(days, 'day')}`;
  }
}
